import dayjs from 'dayjs';
import React, { Fragment, useMemo } from 'react';
import sanitizeHtml from 'sanitize-html';
import style from './AdminEmailPreview.style';
import './AdminEmailPreview.style.css';

const EMAIL_ALLOWED_TAGS = [
  'html',
  'head',
  'body',
  'title',
  'meta',
  'table',
  'tbody',
  'thead',
  'tfoot',
  'tr',
  'td',
  'th',
  'col',
  'colgroup',
  'div',
  'span',
  'p',
  'br',
  'hr',
  'ul',
  'ol',
  'li',
  'a',
  'img',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'strong',
  'em',
  'b',
  'i',
  'u',
  's',
  'strike',
  'del',
  'sub',
  'sup',
  'button',
];

const EMAIL_ALLOWED_ATTRIBUTES = {
  '*': [
    'class',
    'style',
    'id',
    'width',
    'height',
    'align',
    'valign',
    'bgcolor',
    'cellpadding',
    'cellspacing',
    'border',
    'role',
    'aria-*',
    'data-*',
  ],
  a: ['href', 'target', 'rel', 'name', 'style', 'class', 'id'],
  img: ['src', 'alt', 'title', 'style', 'class', 'id', 'width', 'height'],
  meta: ['name', 'content', 'http-equiv', 'charset'],
};

const UNSAFE_CSS_PATTERNS = [
  /@import/gi,
  /expression\s*\(/gi,
  /javascript\s*:/gi,
  /vbscript\s*:/gi,
  /behavior\s*:/gi,
  /-moz-binding/gi,
  /<\/style/gi,
];

const sanitizeCssBlock = (cssText = '') => {
  let cleanedCss = cssText;
  UNSAFE_CSS_PATTERNS.forEach((pattern) => {
    cleanedCss = cleanedCss.replace(pattern, '');
  });
  return cleanedCss.trim();
};

const extractSafeStyles = (rawHtml = '') => {
  if (!rawHtml || typeof document === 'undefined') {
    return '';
  }

  const wrapper = document.createElement('div');
  wrapper.innerHTML = rawHtml;
  const uniqueStyles = new Set();

  wrapper.querySelectorAll('style').forEach((styleTag) => {
    const safeCss = sanitizeCssBlock(styleTag.textContent || '');
    if (safeCss) {
      uniqueStyles.add(safeCss);
    }
  });

  return Array.from(uniqueStyles)
    .map((styleBlock) => `<style>${styleBlock}</style>`)
    .join('');
};

// sanitize-html parses style attributes with postcss, which is a node-only
// dependency. In the browser that parse throws, and sanitize-html responds by
// DELETING the attribute (`delete frame.attribs[a]` in its source). The result is
// that every inline style in the email is silently stripped from the preview -
// the wrapper's fonts, the header background, the logo size constraint, and the
// `margin: 0` this code puts on list paragraphs.
//
// That is why the preview showed loose lists while the editor and the delivered
// email were tight, and it is why AdminEmailPreview.style.css re-implements the
// wrapper by hand: those rules exist to replace styles this call was removing.
//
// Jest runs in node, where postcss works, so this only ever failed in a browser -
// which is why it went unnoticed by the test suite.
export const SANITIZE_OPTIONS = {
  allowedTags: EMAIL_ALLOWED_TAGS,
  allowedAttributes: EMAIL_ALLOWED_ATTRIBUTES,
  allowedSchemes: ['http', 'https', 'mailto', 'tel', 'data'],
  allowedSchemesByTag: {
    img: ['http', 'https', 'data'],
  },
  allowProtocolRelative: false,
  disallowedTagsMode: 'discard',
  // Must stay false. Note that `allowedStyles` cannot be combined with this -
  // sanitize-html throws if both are set.
  parseStyleAttributes: false,
};

export const sanitizeEmailMarkup = (rawHtml = '') => {
  return sanitizeHtml(rawHtml, SANITIZE_OPTIONS);
};

const bodyLooksLikeFullEmail = (rawHtml = '') => {
  return (
    /<html[\s>]/i.test(rawHtml) ||
    /<head[\s>]/i.test(rawHtml) ||
    /<body[\s>]/i.test(rawHtml) ||
    /<meta[^>]+viewport/i.test(rawHtml) ||
    /<table[^>]+class\s*=\s*["'][^"']*(body|container|main)/i.test(rawHtml)
  );
};

const bodyHasInlineEmailLayout = (rawHtml = '') => {
  return (
    /<table[^>]*class\s*=\s*["'][^"']*body[^"']*["'][^>]*style=/i.test(rawHtml) ||
    /<td[^>]*class\s*=\s*["'][^"']*container[^"']*["'][^>]*style=/i.test(rawHtml) ||
    /<table[^>]*class\s*=\s*["'][^"']*main[^"']*["'][^>]*style=/i.test(rawHtml) ||
    /<a[^>]*class\s*=\s*["'][^"']*(register-for-interview|view-schedule|photo-upload)[^"']*["'][^>]*style=/i.test(rawHtml)
  );
};

const extractBodyContentForWrapper = (rawHtml = '') => {
  if (!rawHtml || typeof document === 'undefined') {
    return rawHtml;
  }

  const doc = document.createElement('div');
  doc.innerHTML = rawHtml;

  const directBodyCell = doc.querySelector('table.main > tbody > tr > td.wrapper > table > tbody > tr:nth-child(2) > td.wrapper');
  if (directBodyCell) {
    return directBodyCell.innerHTML;
  }

  const wrapperCells = Array.from(
    doc.querySelectorAll('table.main > tbody > tr > td.wrapper > table > tbody > tr > td.wrapper'),
  );
  if (wrapperCells.length > 0) {
    const contentCellWithButton = wrapperCells.find((cell) =>
      cell.querySelector('a.photo-upload, a.register-for-interview, a.view-schedule'),
    );
    return (contentCellWithButton || wrapperCells[0]).innerHTML;
  }

  const genericBodyCell = Array.from(doc.querySelectorAll('td.wrapper')).find((cell) => {
    return (
      cell.querySelector('a.photo-upload, a.register-for-interview, a.view-schedule') ||
      cell.querySelector('p,ul,ol,div')
    );
  });

  return genericBodyCell ? genericBodyCell.innerHTML : rawHtml;
};

const extractHeaderBackgroundColor = (rawHtml = '') => {
  if (!rawHtml || typeof document === 'undefined') {
    return '';
  }

  const doc = document.createElement('div');
  doc.innerHTML = rawHtml;

  const candidates = [
    ...doc.querySelectorAll('table.main > tbody > tr > td.wrapper > table > tbody > tr:first-child > td'),
    ...doc.querySelectorAll('table.main tr:first-child td'),
    ...doc.querySelectorAll('table tr:first-child td'),
  ];

  for (const node of candidates) {
    const inlineBg = node.style?.backgroundColor;
    if (inlineBg) {
      return inlineBg;
    }

    const attrBg = node.getAttribute('bgcolor');
    if (attrBg) {
      return attrBg;
    }
  }

  return '';
};

const AdminEmailPreview = ({ itemToPreview = {}, mode = 'sent', wrapperHtml = '' }) => {
  const { Body, Subject, DateSent, FirstName, LastName, EmailSentTo } = itemToPreview || {};

  const renderSentTo = () => {
    if (!LastName && !FirstName && !EmailSentTo) {
      return '';
    }
    return `${LastName || ''} ${FirstName ? `, ${FirstName}` : ''} (${EmailSentTo || ''})`;
  };

  const processButtonsInBody = (bodyToUse = '') => {
    if (typeof document === 'undefined') {
      return bodyToUse;
    }

    const doc = document.createElement('div');
    doc.innerHTML = bodyToUse;
    const buttons = doc.querySelectorAll('a.photo-upload, a.register-for-interview, a.view-schedule');

    buttons.forEach((button) => {
      const currentHref = button.getAttribute('href') || '';
      if (!currentHref || /^mailto:|^tel:/i.test(currentHref) || /[?&]preview=/i.test(currentHref)) {
        return;
      }
      const delimiter = currentHref.includes('?') ? '&' : '?';
      button.setAttribute('href', `${currentHref}${delimiter}preview=true`);
    });

    return doc.innerHTML;
  };

  const previewRender = useMemo(() => {
    const rawBody = Body || '';
    const hasFullEmailSkeleton = bodyLooksLikeFullEmail(rawBody);
    const hasInlineLayout = bodyHasInlineEmailLayout(rawBody);
    const shouldRehydrateWithWrapper = Boolean(wrapperHtml && hasFullEmailSkeleton && !hasInlineLayout);
    const emailBodyForWrapper = shouldRehydrateWithWrapper ? extractBodyContentForWrapper(rawBody) : rawBody;

    let composedEmailMarkup = emailBodyForWrapper;

    if ((!hasFullEmailSkeleton && wrapperHtml) || shouldRehydrateWithWrapper) {
      composedEmailMarkup = wrapperHtml.includes('{EMAILBODY}')
        ? wrapperHtml.replace('{EMAILBODY}', emailBodyForWrapper)
        : `${wrapperHtml}${emailBodyForWrapper}`;
    }

    const safeStyles = extractSafeStyles(`${wrapperHtml || ''}\n${emailBodyForWrapper}`);
    const sanitizedMarkup = sanitizeEmailMarkup(composedEmailMarkup);
    const html = processButtonsInBody(`${safeStyles}${sanitizedMarkup}`);
    const headerBgColor =
      extractHeaderBackgroundColor(composedEmailMarkup) ||
      extractHeaderBackgroundColor(wrapperHtml) ||
      extractHeaderBackgroundColor(rawBody);

    return { html, headerBgColor };
  }, [Body, wrapperHtml]);

  return (
    <Fragment>
      {mode === 'sent' ? (
        <div className="comm-preview-meta-card">
          <div style={style.simpleRow} className="comm-preview-meta-row">
            <div style={{ ...style.simpleColumn, width: 110 }} className="comm-preview-meta-label">
              <strong>Subject:</strong>
            </div>
            <div style={style.simpleColumn} className="comm-preview-meta-value">
              {Subject || ''}
            </div>
          </div>
          <div style={style.simpleRow} className="comm-preview-meta-row">
            <div style={{ ...style.simpleColumn, width: 110 }} className="comm-preview-meta-label">
              <strong>Date Sent:</strong>
            </div>
            <div style={style.simpleColumn} className="comm-preview-meta-value">
              {DateSent ? dayjs(DateSent).format('dddd, MMMM DD, YYYY hh:mm A') : ' '}
            </div>
          </div>
          <div style={style.simpleRow} className="comm-preview-meta-row">
            <div style={{ ...style.simpleColumn, width: 110 }} className="comm-preview-meta-label">
              <strong>Sent To:</strong>
            </div>
            <div style={style.simpleColumn} className="comm-preview-meta-value">
              {renderSentTo()}
            </div>
          </div>
        </div>
      ) : null}
      <div className="comm-preview-canvas-wrap">
        <div className="comm-preview-html-host">
          <div
            className="comm-preview-email-body"
            style={previewRender.headerBgColor ? { '--comm-preview-header-bg': previewRender.headerBgColor } : undefined}
            dangerouslySetInnerHTML={{ __html: previewRender.html }}
          />
        </div>
      </div>
    </Fragment>
  );
};

export default AdminEmailPreview;
